import { msToPixel, msToRulerLabel } from './timeUtils';
import type { Track, Clip, EditorProject, EffectClip } from '../types';
import type { PeakData } from '../engine/peakGenerator';

/**
 * Lightweight accessor for drawing functions that need asset metadata.
 * Set by TimelineCanvas before each draw call.
 */
let _projectRef: EditorProject | null = null;
export function setDrawingProject(project: EditorProject) { _projectRef = project; }
function getDrawingProject() { return _projectRef; }

const RULER_HEIGHT = 28;
const TRACK_HEIGHT = 56;
const TRACK_GAP = 4;
const TRACK_LABEL_WIDTH = 48;
/** Left padding inside the canvas so the playhead handle + first label aren't clipped */
const CONTENT_PAD_LEFT = 12;
const WAVEFORM_COLOR = 'rgba(7, 229, 176, 0.6)';
const VIDEO_CLIP_BG = '#2A2A2E';
const VIDEO_CLIP_BORDER = '#48484A';
const AUDIO_CLIP_BG = 'rgba(7, 229, 176, 0.12)';
const AUDIO_CLIP_BORDER = '#07E5B0';
const RULER_BG = '#0A0A0A';
const RULER_TEXT = '#8E8E93';
const TRACK_BG = '#141416';
const TRACK_BG_ALT = '#1A1A1E';
const PLAYHEAD_COLOR = '#07E5B0';

// Effect track constants
const EFFECT_TRACK_HEIGHT = 36;
const EFFECT_CLIP_BG = 'rgba(168, 85, 247, 0.15)';
const EFFECT_CLIP_BG_ACTIVE = 'rgba(168, 85, 247, 0.3)';
const EFFECT_CLIP_BORDER = '#A855F7';

/** Compute Y position of a track accounting for variable heights */
export function getTrackY(tracks: Track[], trackIndex: number): number {
  let y = RULER_HEIGHT;
  for (let i = 0; i < trackIndex; i++) {
    y += getTrackHeight(tracks[i]) + TRACK_GAP;
  }
  return y;
}

/** Get the height of a track based on its type */
export function getTrackHeight(track: Track): number {
  return track.type === 'effect' ? EFFECT_TRACK_HEIGHT : TRACK_HEIGHT;
}

/** Total canvas height needed for all tracks */
export function getTotalTracksHeight(tracks: Track[]): number {
  if (tracks.length === 0) return RULER_HEIGHT;
  let h = RULER_HEIGHT;
  for (let i = 0; i < tracks.length; i++) {
    h += getTrackHeight(tracks[i]) + TRACK_GAP;
  }
  return h;
}

export interface DrawContext {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  pxPerMs: number;
  scrollOffsetMs: number;
  durationMs: number;
}

export function drawRuler(dc: DrawContext) {
  const { ctx, width, pxPerMs, scrollOffsetMs } = dc;

  ctx.fillStyle = RULER_BG;
  ctx.fillRect(0, 0, width, RULER_HEIGHT);

  const msPerPx = 1 / pxPerMs;
  let tickIntervalMs = 1000;
  if (msPerPx > 20) tickIntervalMs = 10000;
  else if (msPerPx > 5) tickIntervalMs = 5000;
  else if (msPerPx < 1) tickIntervalMs = 500;

  const startMs = Math.floor(scrollOffsetMs / tickIntervalMs) * tickIntervalMs;
  const endMs = scrollOffsetMs + width / pxPerMs;

  ctx.font = '10px ui-monospace, "SF Mono", monospace';
  ctx.textAlign = 'center';

  for (let ms = startMs; ms <= endMs; ms += tickIntervalMs) {
    const x = msToPixel(ms, pxPerMs, scrollOffsetMs);
    if (x < 0 || x > width) continue;

    ctx.fillStyle = '#48484A';
    ctx.fillRect(x, RULER_HEIGHT - 8, 1, 8);

    ctx.fillStyle = RULER_TEXT;
    ctx.fillText(msToRulerLabel(ms), x, RULER_HEIGHT - 12);

    const minorInterval = tickIntervalMs / 4;
    for (let m = 1; m < 4; m++) {
      const mx = msToPixel(ms + m * minorInterval, pxPerMs, scrollOffsetMs);
      if (mx >= 0 && mx <= width) {
        ctx.fillStyle = '#3A3A3C';
        ctx.fillRect(mx, RULER_HEIGHT - 4, 1, 4);
      }
    }
  }
}

export function drawTrackLanes(dc: DrawContext, tracks: Track[]) {
  const { ctx, width } = dc;

  tracks.forEach((track, i) => {
    const y = getTrackY(tracks, i);
    const h = getTrackHeight(track);

    ctx.fillStyle = i % 2 === 0 ? TRACK_BG : TRACK_BG_ALT;
    ctx.fillRect(0, y, width, h);
  });
}

export function drawClipBlocks(
  dc: DrawContext,
  tracks: Track[],
  selectedClipId: string | null,
) {
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  tracks.forEach((track, trackIndex) => {
    if (track.type === 'effect') return; // Effect clips drawn separately
    const trackY = getTrackY(tracks, trackIndex);
    const trackH = getTrackHeight(track);
    const isVideo = track.type === 'video';

    track.clips.forEach((clip) => {
      const x = Math.max(0, msToPixel(clip.startMs, pxPerMs, scrollOffsetMs));
      const xEnd = msToPixel(clip.startMs + clip.durationMs, pxPerMs, scrollOffsetMs);
      const w = xEnd - x;
      if (w <= 0) return;

      const isSelected = clip.id === selectedClipId;
      const clipY = trackY + 4;
      const clipH = trackH - 8;

      // Clip background
      ctx.fillStyle = isVideo
        ? (isSelected ? 'rgba(72, 72, 74, 0.5)' : VIDEO_CLIP_BG)
        : (isSelected ? 'rgba(7, 229, 176, 0.2)' : AUDIO_CLIP_BG);
      roundRect(ctx, x, clipY, w, clipH, 6);
      ctx.fill();

      // Clip border
      ctx.strokeStyle = isVideo
        ? (isSelected ? '#07E5B0' : VIDEO_CLIP_BORDER)
        : (isSelected ? '#07E5B0' : AUDIO_CLIP_BORDER);
      ctx.lineWidth = isSelected ? 2 : 1;
      roundRect(ctx, x, clipY, w, clipH, 6);
      ctx.stroke();

      // Video clip: placeholder gradient (thumbnails drawn separately)
      if (isVideo && w > 20) {
        ctx.save();
        ctx.beginPath();
        roundRect(ctx, x, clipY, w, clipH, 6);
        ctx.clip();
        const grad = ctx.createLinearGradient(x, clipY, x + w, clipY);
        grad.addColorStop(0, 'rgba(255,255,255,0.02)');
        grad.addColorStop(0.5, 'rgba(255,255,255,0.04)');
        grad.addColorStop(1, 'rgba(255,255,255,0.02)');
        ctx.fillStyle = grad;
        ctx.fillRect(x, clipY, w, clipH);
        ctx.restore();
      }
    });
  });
}

/**
 * Draw video thumbnails (filmstrip) inside a video clip.
 */
export function drawThumbnails(
  dc: DrawContext,
  thumbnails: ImageBitmap[],
  clip: Clip,
  trackIndex: number,
  thumbIntervalMs: number = 500,
  tracks?: Track[],
) {
  if (thumbnails.length === 0) return;
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  const clipXRaw = msToPixel(clip.startMs, pxPerMs, scrollOffsetMs);
  const clipXEnd = msToPixel(clip.startMs + clip.durationMs, pxPerMs, scrollOffsetMs);
  const clipX = Math.max(0, clipXRaw);
  const clipW = clipXEnd - clipX;
  if (clipW <= 0) return;

  const trackY = tracks ? getTrackY(tracks, trackIndex) : RULER_HEIGHT + trackIndex * (TRACK_HEIGHT + TRACK_GAP);
  const trackH = tracks ? getTrackHeight(tracks[trackIndex]) : TRACK_HEIGHT;
  const clipY = trackY + 4;
  const clipH = trackH - 8;

  const tilePx = thumbIntervalMs * pxPerMs;

  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.beginPath();
  roundRect(ctx, clipX, clipY, clipW, clipH, 6);
  ctx.clip();

  const firstThumbIdx = Math.floor(clip.sourceOffsetMs / thumbIntervalMs);

  for (let i = firstThumbIdx; i < thumbnails.length; i++) {
    const thumb = thumbnails[i];
    const sourceMs = i * thumbIntervalMs;
    const tileX = msToPixel(clip.startMs + (sourceMs - clip.sourceOffsetMs), pxPerMs, scrollOffsetMs);
    const tileEnd = tileX + tilePx;

    if (tileX > clipXEnd) break;
    if (tileEnd < clipX) continue;

    const srcAspect = thumb.width / thumb.height;
    const dstAspect = tilePx / clipH;

    let sx = 0, sy = 0, sw = thumb.width, sh = thumb.height;
    if (srcAspect > dstAspect) {
      sw = Math.round(thumb.height * dstAspect);
      sx = Math.round((thumb.width - sw) / 2);
    } else {
      sh = Math.round(thumb.width / dstAspect);
      sy = Math.round((thumb.height - sh) / 2);
    }

    ctx.drawImage(thumb, sx, sy, sw, sh, tileX, clipY, tilePx, clipH);
  }

  ctx.restore();
}

export function drawWaveform(
  dc: DrawContext,
  peaks: PeakData | Float32Array,
  clip: Clip,
  trackIndex: number,
  tracks?: Track[],
) {
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  const clipXRaw = msToPixel(clip.startMs, pxPerMs, scrollOffsetMs);
  const clipX = Math.max(0, clipXRaw);
  const clipXEnd = msToPixel(clip.startMs + clip.durationMs, pxPerMs, scrollOffsetMs);
  const clipW = clipXEnd - clipX;
  if (clipW <= 0) return;

  const trackY = tracks ? getTrackY(tracks, trackIndex) : RULER_HEIGHT + trackIndex * (TRACK_HEIGHT + TRACK_GAP);
  const trackH = tracks ? getTrackHeight(tracks[trackIndex]) : TRACK_HEIGHT;
  const centerY = trackY + trackH / 2;
  const halfHeight = (trackH - 16) / 2;

  const asset = getDrawingProject()?.assets.find((a) => a.id === clip.assetId);
  const assetDurationMs = asset?.durationMs || clip.durationMs;
  const totalAssetPx = assetDurationMs * pxPerMs;
  const sourceOffsetPx = clip.sourceOffsetMs * pxPerMs;
  const visualOffset = clipX > clipXRaw ? (clipX - clipXRaw) : 0;

  // New PeakData format: interleaved min/max pairs
  if ('peaks' in peaks && 'samplesPerPixel' in peaks) {
    const pd = peaks as PeakData;
    const numPeaks = pd.peaks.length / 2;
    const peaksPerPx = numPeaks / totalAssetPx;

    ctx.fillStyle = WAVEFORM_COLOR;

    for (let px = 0; px < clipW; px++) {
      const peakIdx = Math.floor((sourceOffsetPx + visualOffset + px) * peaksPerPx);
      if (peakIdx >= numPeaks) break;

      const min = pd.peaks[peakIdx * 2];
      const max = pd.peaks[peakIdx * 2 + 1];

      const yMin = centerY - max * halfHeight;
      const yMax = centerY - min * halfHeight;
      const barHeight = Math.max(1, yMax - yMin);

      ctx.fillRect(clipX + px, yMin, 1, barHeight);
    }
    return;
  }

  // Legacy Float32Array format (single amplitude values)
  const legacyPeaks = peaks as Float32Array;
  const peaksPerPx = legacyPeaks.length / totalAssetPx;

  ctx.fillStyle = WAVEFORM_COLOR;

  for (let px = 0; px < clipW; px++) {
    const peakIdx = Math.floor((sourceOffsetPx + visualOffset + px) * peaksPerPx);
    if (peakIdx >= legacyPeaks.length) break;

    const amplitude = Math.min(legacyPeaks[peakIdx] * 3, 1);
    const barHeight = amplitude * halfHeight;

    const x = clipX + px;
    ctx.fillRect(x, centerY - barHeight, 1, barHeight * 2);
  }
}

export function drawPlayhead(dc: DrawContext, currentTimeMs: number) {
  const { ctx, height, pxPerMs, scrollOffsetMs } = dc;
  const x = msToPixel(currentTimeMs, pxPerMs, scrollOffsetMs);

  if (x < 0 || x > dc.width) return;

  // Playhead line
  ctx.fillStyle = PLAYHEAD_COLOR;
  ctx.fillRect(x - 1, 0, 2, height);

  // Playhead handle (rounded rect at top)
  const handleW = 12;
  const handleH = 16;
  ctx.fillStyle = PLAYHEAD_COLOR;
  roundRect(ctx, x - handleW / 2, 0, handleW, handleH, 3);
  ctx.fill();

  // Triangle pointer at bottom of handle
  ctx.beginPath();
  ctx.moveTo(x - 4, handleH);
  ctx.lineTo(x + 4, handleH);
  ctx.lineTo(x, handleH + 5);
  ctx.closePath();
  ctx.fill();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

const TRIM_HANDLE_WIDTH = 6;
const TRIM_HANDLE_COLOR = '#07E5B0';

export function drawTrimHandles(
  dc: DrawContext,
  tracks: Track[],
  selectedClipId: string | null,
) {
  if (!selectedClipId) return;
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
    const track = tracks[trackIndex];

    // Check regular clips
    const clip = track.clips.find((c) => c.id === selectedClipId);
    // Check effect clips
    const effectClip = track.effectClips?.find((ec) => ec.id === selectedClipId);
    const foundClip = clip || effectClip;
    if (!foundClip) continue;

    const trackY = getTrackY(tracks, trackIndex);
    const trackH = getTrackHeight(track);
    const clipX = Math.max(0, msToPixel(foundClip.startMs, pxPerMs, scrollOffsetMs));
    const clipXEnd = msToPixel(foundClip.startMs + foundClip.durationMs, pxPerMs, scrollOffsetMs);
    const clipY = trackY + 4;
    const clipH = trackH - 8;

    if (clipXEnd - clipX <= TRIM_HANDLE_WIDTH * 2) break;

    ctx.fillStyle = TRIM_HANDLE_COLOR;

    // Left handle
    roundRect(ctx, clipX, clipY, TRIM_HANDLE_WIDTH, clipH, 3);
    ctx.fill();

    // Right handle
    roundRect(ctx, clipXEnd - TRIM_HANDLE_WIDTH, clipY, TRIM_HANDLE_WIDTH, clipH, 3);
    ctx.fill();

    break;
  }
}

export function hitTestTrimHandle(
  x: number,
  y: number,
  tracks: Track[],
  selectedClipId: string | null,
  pxPerMs: number,
  scrollOffsetMs: number,
): 'left' | 'right' | null {
  if (!selectedClipId) return null;

  for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
    const track = tracks[trackIndex];
    const clip = track.clips.find((c) => c.id === selectedClipId);
    const effectClip = track.effectClips?.find((ec) => ec.id === selectedClipId);
    const foundClip = clip || effectClip;
    if (!foundClip) continue;

    const trackY = getTrackY(tracks, trackIndex);
    const trackH = getTrackHeight(track);
    const clipX = Math.max(0, msToPixel(foundClip.startMs, pxPerMs, scrollOffsetMs));
    const clipXEnd = msToPixel(foundClip.startMs + foundClip.durationMs, pxPerMs, scrollOffsetMs);
    const clipY = trackY + 4;
    const clipH = trackH - 8;

    if (y < clipY || y > clipY + clipH) return null;

    if (x >= clipX && x <= clipX + TRIM_HANDLE_WIDTH + 4) return 'left';
    if (x >= clipXEnd - TRIM_HANDLE_WIDTH - 4 && x <= clipXEnd) return 'right';

    return null;
  }

  return null;
}

// --- Phase 4: Fade overlays ---

const FADE_OVERLAY_COLOR = 'rgba(0, 0, 0, 0.35)';
const FADE_CURVE_COLOR = '#07E5B0';
const FADE_HANDLE_SIZE = 8;

export function drawFadeOverlay(dc: DrawContext, clip: Clip, trackIndex: number, tracks?: Track[]) {
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  const clipX = Math.max(0, msToPixel(clip.startMs, pxPerMs, scrollOffsetMs));
  const clipXEnd = msToPixel(clip.startMs + clip.durationMs, pxPerMs, scrollOffsetMs);
  const trackY = tracks ? getTrackY(tracks, trackIndex) : RULER_HEIGHT + trackIndex * (TRACK_HEIGHT + TRACK_GAP);
  const trackH = tracks ? getTrackHeight(tracks[trackIndex]) : TRACK_HEIGHT;
  const clipY = trackY + 4;
  const clipH = trackH - 8;

  // Fade in
  if (clip.fadeIn && clip.fadeIn.durationMs > 0) {
    const fadePx = clip.fadeIn.durationMs * pxPerMs;
    const fadeEnd = clipX + fadePx;

    // Dark overlay
    ctx.fillStyle = FADE_OVERLAY_COLOR;
    ctx.beginPath();
    ctx.moveTo(clipX, clipY);
    ctx.lineTo(fadeEnd, clipY);
    ctx.lineTo(clipX, clipY + clipH);
    ctx.closePath();
    ctx.fill();

    // Curve line
    ctx.strokeStyle = FADE_CURVE_COLOR;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(clipX, clipY + clipH);
    ctx.lineTo(fadeEnd, clipY);
    ctx.stroke();

    // Handle dot
    ctx.fillStyle = FADE_CURVE_COLOR;
    ctx.beginPath();
    ctx.arc(fadeEnd, clipY, FADE_HANDLE_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  // Fade out
  if (clip.fadeOut && clip.fadeOut.durationMs > 0) {
    const fadePx = clip.fadeOut.durationMs * pxPerMs;
    const fadeStart = clipXEnd - fadePx;

    // Dark overlay
    ctx.fillStyle = FADE_OVERLAY_COLOR;
    ctx.beginPath();
    ctx.moveTo(fadeStart, clipY);
    ctx.lineTo(clipXEnd, clipY);
    ctx.lineTo(clipXEnd, clipY + clipH);
    ctx.closePath();
    ctx.fill();

    // Curve line
    ctx.strokeStyle = FADE_CURVE_COLOR;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(fadeStart, clipY);
    ctx.lineTo(clipXEnd, clipY + clipH);
    ctx.stroke();

    // Handle dot
    ctx.fillStyle = FADE_CURVE_COLOR;
    ctx.beginPath();
    ctx.arc(fadeStart, clipY, FADE_HANDLE_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function hitTestFadeHandle(
  x: number,
  y: number,
  tracks: Track[],
  selectedClipId: string | null,
  pxPerMs: number,
  scrollOffsetMs: number,
): 'fade-in' | 'fade-out' | null {
  if (!selectedClipId) return null;

  for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
    const track = tracks[trackIndex];
    const clip = track.clips.find((c) => c.id === selectedClipId);
    if (!clip) continue;

    const clipX = Math.max(0, msToPixel(clip.startMs, pxPerMs, scrollOffsetMs));
    const clipXEnd = msToPixel(clip.startMs + clip.durationMs, pxPerMs, scrollOffsetMs);
    const trackY = getTrackY(tracks, trackIndex);
    const clipY = trackY + 4;

    const hitRadius = FADE_HANDLE_SIZE;

    // Fade in handle
    if (clip.fadeIn && clip.fadeIn.durationMs > 0) {
      const handleX = clipX + clip.fadeIn.durationMs * pxPerMs;
      if (Math.abs(x - handleX) < hitRadius && Math.abs(y - clipY) < hitRadius) {
        return 'fade-in';
      }
    }

    // Fade out handle
    if (clip.fadeOut && clip.fadeOut.durationMs > 0) {
      const handleX = clipXEnd - clip.fadeOut.durationMs * pxPerMs;
      if (Math.abs(x - handleX) < hitRadius && Math.abs(y - clipY) < hitRadius) {
        return 'fade-out';
      }
    }

    return null;
  }

  return null;
}

// --- Effect Clips (Region-based Event FX) ---

export function drawEffectClips(
  dc: DrawContext,
  tracks: Track[],
  selectedClipId: string | null,
  currentTimeMs: number,
) {
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  tracks.forEach((track, trackIndex) => {
    if (track.type !== 'effect' || !track.effectClips) return;

    const trackY = getTrackY(tracks, trackIndex);
    const trackH = EFFECT_TRACK_HEIGHT;

    for (const ec of track.effectClips) {
      const x = Math.max(0, msToPixel(ec.startMs, pxPerMs, scrollOffsetMs));
      const xEnd = msToPixel(ec.startMs + ec.durationMs, pxPerMs, scrollOffsetMs);
      const w = xEnd - x;
      if (w <= 0) continue;

      const isSelected = ec.id === selectedClipId;
      const isActive = currentTimeMs >= ec.startMs && currentTimeMs < ec.startMs + ec.durationMs;
      const clipY = trackY + 3;
      const clipH = trackH - 6;

      // Background
      ctx.fillStyle = ec.bypassed
        ? 'rgba(168, 85, 247, 0.08)'
        : isActive ? EFFECT_CLIP_BG_ACTIVE : EFFECT_CLIP_BG;
      roundRect(ctx, x, clipY, w, clipH, 5);
      ctx.fill();

      // Border
      ctx.strokeStyle = isSelected ? '#07E5B0' : ec.bypassed ? 'rgba(168, 85, 247, 0.3)' : EFFECT_CLIP_BORDER;
      ctx.lineWidth = isSelected ? 2 : 1;
      roundRect(ctx, x, clipY, w, clipH, 5);
      ctx.stroke();

      // Label
      if (w > 30) {
        ctx.fillStyle = ec.bypassed ? 'rgba(168, 85, 247, 0.4)' : '#A855F7';
        ctx.font = '9px ui-monospace, "SF Mono", monospace';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        const label = ec.effectType.charAt(0).toUpperCase() + ec.effectType.slice(1);
        ctx.fillText(label, x + 6, clipY + clipH / 2, w - 12);
      }
    }
  });
}

// --- Phase 9: Loop selection region ---

const SELECTION_COLOR = 'rgba(7, 229, 176, 0.1)';
const SELECTION_BORDER_COLOR = '#07E5B0';

export function drawSelectionRegion(
  dc: DrawContext,
  startMs: number,
  endMs: number,
  loopEnabled: boolean,
) {
  const { ctx, height, pxPerMs, scrollOffsetMs } = dc;
  const x1 = msToPixel(startMs, pxPerMs, scrollOffsetMs);
  const x2 = msToPixel(endMs, pxPerMs, scrollOffsetMs);

  if (x2 <= 0 || x1 >= dc.width) return;

  const left = Math.max(0, x1);
  const right = Math.min(dc.width, x2);
  const w = right - left;

  // Selection highlight
  ctx.fillStyle = loopEnabled ? SELECTION_COLOR : 'rgba(255, 255, 255, 0.05)';
  ctx.fillRect(left, RULER_HEIGHT, w, height - RULER_HEIGHT);

  // Borders
  ctx.strokeStyle = loopEnabled ? SELECTION_BORDER_COLOR : 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 1;
  ctx.setLineDash(loopEnabled ? [] : [4, 4]);
  ctx.beginPath();
  ctx.moveTo(left, RULER_HEIGHT);
  ctx.lineTo(left, height);
  ctx.moveTo(right, RULER_HEIGHT);
  ctx.lineTo(right, height);
  ctx.stroke();
  ctx.setLineDash([]);

  // Ruler highlight
  ctx.fillStyle = loopEnabled ? 'rgba(7, 229, 176, 0.3)' : 'rgba(255, 255, 255, 0.1)';
  ctx.fillRect(left, 0, w, RULER_HEIGHT);
}

export { RULER_HEIGHT, TRACK_HEIGHT, TRACK_GAP, TRACK_LABEL_WIDTH, TRIM_HANDLE_WIDTH, EFFECT_TRACK_HEIGHT };
